/**
 * The application's display notation, and the parser that reads it back out of stored
 * documents. Renders Dutch notation today; a second locale changes this file, not its call
 * sites.
 *
 * Not built on Intl.NumberFormat: Intl renders a negative amount as "€ -1.000,00" and
 * cannot express the ",-" rule. Mirrored by app/Domain/Shared/Support/DisplayFormat.php.
 *
 * Keep free of Pinia, window and lodash — the document editor imports it.
 */

export const NBSP = '\u00A0'

const NNBSP = '\u202F'

const normalizeSpaces = (text: string): string => text.split(NBSP).join(' ').split(NNBSP).join(' ')

/** A comma always decides; a lone dot groups in "1.000" but is decimal in "12.50". */
const toDecimalPoint = (digits: string): string => {
  if (digits.includes(',')) return digits.split('.').join('').replace(',', '.')

  if (!digits.includes('.')) return digits

  return /^\d{1,3}(\.\d{3})+$/.test(digits) ? digits.split('.').join('') : digits
}

const trimTrailingZeros = (formatted: string): string =>
  formatted.includes(',') ? formatted.replace(/0+$/, '').replace(/,$/, '') : formatted

const group = (whole: string): string => whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

const fixed = (value: number, decimals: number): string => {
  const [whole, fraction] = Math.abs(value).toFixed(decimals).split('.')

  return fraction ? `${group(whole)},${fraction}` : group(whole)
}

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number') return Number.isFinite(value) ? value : null

  return parseNumber(value)
}

/**
 * The first number in a string, in any notation this application has written: NBSP or
 * plain space, ",-" / ",–" / ",00", dot thousands groups, a bare dot decimal, and trailing
 * labels ("min. 3", "/ km"). Null when there is no number — not 0.
 */
export function parseNumber(text: string | number | null | undefined): number | null {
  if (typeof text === 'number') return Number.isFinite(text) ? text : null

  if (text === null || text === undefined || text.trim() === '') return null

  const normalized = normalizeSpaces(text).replace(/,[-–](?!\d)/g, ',0')
  const match = normalized.match(/-?\s*€?\s*-?\d[\d.,]*/)

  if (!match) return null

  const token = match[0]
  const digits = token.replace(/[^\d.,]/g, '').replace(/[.,]+$/, '')

  if (digits === '') return null

  const number = parseFloat(toDecimalPoint(digits))

  if (!Number.isFinite(number)) return null

  return token.includes('-') ? -number : number
}

/** The count following `label` ("min. 3" with label "min." reads 3), non-breaking spaces included. */
export function parseTrailingCount(text: string | null | undefined, label: string): number | null {
  if (text === null || text === undefined) return null

  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = normalizeSpaces(text).match(new RegExp(`${escaped}\\s*([\\d.,]+)`))

  if (!match) return null

  const count = parseNumber(match[1])

  return count === null ? null : Math.trunc(count)
}

/** 1 · 1,5 · 1.000 · 1.000,5 — trailing zeros are dropped. */
export function formatNumber(value: number | string | null | undefined, maxDecimals: number = 2): string {
  const number = toNumber(value)

  if (number === null) return ''

  return `${number < 0 ? '-' : ''}${trimTrailingZeros(fixed(number, maxDecimals))}`
}

/** € 1,- · € 1,50 · € 1.000,- · -€ 1.000,- — rounded amounts end on ",-". */
export function formatCurrency(value: number | string | null | undefined, options: { symbol?: boolean } = {}): string {
  const number = toNumber(value)

  if (number === null) return ''

  const amount = fixed(number, 2).replace(/,00$/, ',-')
  const sign = number < 0 ? '-' : ''

  return options.symbol === false ? `${sign}${amount}` : `${sign}€${NBSP}${amount}`
}

/** Spaces an amount or a label cannot break on, so a symbol never ends up alone on a line. */
export const nonBreaking = (text: string): string => text.replace(/ /g, NBSP)

/** Everything an amount is made of apart from its label. Shared with DisplayFormat::AMOUNT_LABEL_PATTERN. */
export const AMOUNT_LABEL_PATTERN = '[\\s\\u00A0\\u202F\\d.,-]+'

/** What is left of an amount once digits and separators are gone: the currency symbol or the unit behind it. */
const amountLabel = (text: string): string => text.replace(new RegExp(AMOUNT_LABEL_PATTERN, 'gu'), '')

/**
 * Whether two amounts print the same value: "€ 142,00" and "€ 142,-" do, "25 u." and "€ 25,-" do not.
 * Mirrors DisplayFormat::isSameAmount().
 */
export function isSameAmount(current: string, value: string): boolean {
  if (amountLabel(current) !== amountLabel(value)) return false

  const currentNumber = parseNumber(current)
  const newNumber = parseNumber(value)

  if (currentNumber === null || newNumber === null) return currentNumber === newNumber

  return Math.abs(currentNumber - newNumber) < 0.00001
}

/** 21% · 1,5% — the value as stored. */
export function formatPercentage(value: number | string | null | undefined, maxDecimals: number = 2): string {
  const number = formatNumber(value, maxDecimals)

  return number === '' ? '' : `${number}%`
}
